/**
 * Components for displaying chat messages in different styles
 */

import { memo, useMemo } from 'react';
import { useChatBubble } from '../../model/useChatBubble';
import { ChatMarkdownRenderer } from '../ChatMarkdownRenderer';
import { MessageAttachments } from '../ChatMessage/MessageAttachments';
import { ErrorMetadata } from '../ErrorMetadata/ErrorMetadata';
import { AgentResponse } from './AgentResponse/AgentResponse';
import { AgentResponseSkeleton } from './AgentResponse/AgentResponseSkeleton';
import { ChatBubbleProps } from './ChatBubble.types';
import { UserBubble } from './UserBubble';
import { useTranslation } from 'react-i18next';

export const ChatBubble = memo(
  ({
    content,
    type,
    typewriter = true,
    agentId,
    messageId,
    hasWidget = false,
    files,
    isStreaming = false,
    metadata,
    steps,
  }: ChatBubbleProps) => {
    const { t } = useTranslation('chats');

    const { renderingState, processedText } = useChatBubble({ content, type, agentId });

    // Memoize the disableAnimation calculation
    const disableAnimation = useMemo(() => {
      return hasWidget;
    }, [hasWidget]);

    if (renderingState.shouldRenderUserBubble) {
      return <UserBubble content={content} files={files ?? []} metadata={metadata} />;
    }

    if (renderingState.shouldRenderSkeleton) {
      return <AgentResponseSkeleton />;
    }

    if (renderingState.shouldRenderError) {
      return (
        <AgentResponse agentId={agentId} messageId={messageId} hasWidget={hasWidget}>
          {steps}
          <span data-testid="agent-response-error">
            {t('chats.chatBubble.weAreUnableToProcess')}
          </span>
        </AgentResponse>
      );
    }

    return (
      <AgentResponse agentId={agentId} messageId={messageId} hasWidget={hasWidget}>
        {steps}
        <ChatMarkdownRenderer
          content={processedText}
          typewriter={typewriter}
          disableAnimation={disableAnimation}
          isStreaming={isStreaming}
        />
        <MessageAttachments files={files} type="assistant" agentId={agentId} />
        {metadata?.issue && <ErrorMetadata issue={metadata.issue} />}
      </AgentResponse>
    );
  },
);
ChatBubble.displayName = 'ChatBubble';
